/**
 * Utility functions for formatting values
 */

// ============================================================================
// LOGGING UTILITIES
// ============================================================================

/**
 * Development-only logger that doesn't output in production
 */
const isDev = process.env.NODE_ENV === 'development';

export const devLog = (...args) => {
  if (isDev) {
    console.log(...args);
  }
};

export const devWarn = (...args) => {
  if (isDev) {
    console.warn(...args);
  }
};

export const devError = (...args) => {
  // Always log errors, but with more context in dev
  console.error(...args);
};

// ============================================================================
// CURRENCY FORMATTING
// ============================================================================

/**
 * Format number as currency (from dollars)
 * @param {number} value - Value in dollars
 * @param {string} currency - Currency code (default: USD)
 * @param {boolean} showCents - Show cents in output
 */
export const formatCurrency = (value, currency = 'USD', showCents = false) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0
  }).format(value);
};

/**
 * Format cents as currency (from cents)
 * @param {number} cents - Value in cents
 * @param {boolean} showCents - Show cents in output (default: false)
 */
export const formatCurrencyFromCents = (cents, showCents = false) => {
  if (!cents && cents !== 0) return '$0';
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0
  }).format(dollars);
};

/**
 * Format cents as compact currency (e.g., $1.5M, $500K)
 * @param {number} cents - Value in cents
 */
export const formatCompactCurrency = (cents) => {
  if (!cents && cents !== 0) return '$0';
  const dollars = cents / 100;
  
  if (dollars >= 1000000000) {
    return `$${(dollars / 1000000000).toFixed(1)}B`;
  } else if (dollars >= 1000000) {
    return `$${(dollars / 1000000).toFixed(1)}M`;
  } else if (dollars >= 1000) {
    return `$${(dollars / 1000).toFixed(1)}K`;
  }
  return `$${dollars.toFixed(0)}`;
};

/**
 * Format number with commas
 */
export const formatNumber = (value) => {
  return new Intl.NumberFormat('en-US').format(value);
};

/**
 * Format date
 */
export const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format percentage
 */
export const formatPercentage = (value, decimals = 2) => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Calculate days ago
 */
export const daysAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};
